import { supabase } from "@/integrations/supabase/client";
import { UserProfile } from "@/types/userTypes";

export interface StudentReportData {
  id: string;
  name: string;
  email: string;
  totalCourses: number;
  activeCourses: number;
  inProgressCourses: number;
  completedCourses: number;
  certificatesEarned: number;
  averageGrade: number | null;
  totalTimeSpent: number;
  lastActivityDate: string | null;
  completionRate: number;
}

export interface CourseAssignmentDetail {
  id: string;
  studentName: string;
  studentEmail: string;
  courseId: string;
  courseTitle: string;
  category: string;
  level: string;
  assignedDate: string;
  status: string;
  completionDate: string | null;
  finalGrade: number | null;
  timeSpent: number;
  hasCertificate: boolean;
}

export interface ReportFilters {
  studentId?: string;
  courseIds?: string[];
  statuses?: string[];
  dateFrom?: string;
  dateTo?: string;
  searchQuery?: string;
}

export const studentReportingService = {
  async fetchAllStudentsReport(filters?: ReportFilters): Promise<StudentReportData[]> {
    try {
      // Fetch all students with their profiles
      const { data: profiles, error: profileError } = await supabase
        .from('profiles')
        .select('id, name, email')
        .order('name');

      if (profileError) throw profileError;
      if (!profiles) return [];

      // Fetch all course assignments
      let assignmentQuery = supabase
        .from('course_assignments')
        .select(`
          id,
          user_id,
          course_id,
          status,
          assigned_date,
          completion_date,
          final_grade,
          total_time_spent,
          last_activity_date
        `);

      // Apply filters
      if (filters?.dateFrom) {
        assignmentQuery = assignmentQuery.gte('assigned_date', filters.dateFrom);
      }
      if (filters?.dateTo) {
        assignmentQuery = assignmentQuery.lte('assigned_date', filters.dateTo);
      }
      if (filters?.statuses && filters.statuses.length > 0) {
        assignmentQuery = assignmentQuery.in('status', filters.statuses);
      }
      if (filters?.courseIds && filters.courseIds.length > 0) {
        assignmentQuery = assignmentQuery.in('course_id', filters.courseIds);
      }

      const { data: assignments, error: assignmentError } = await assignmentQuery;
      if (assignmentError) throw assignmentError;

      // Fetch certificates
      const { data: certificates, error: certError } = await supabase
        .from('certificates')
        .select('user_id, course_id');

      if (certError) throw certError;

      // Process data for each student
      const reportData: StudentReportData[] = profiles.map(profile => {
        const userAssignments = assignments?.filter(a => a.user_id === profile.id) || [];
        const userCertificates = certificates?.filter(c => c.user_id === profile.id) || [];

        const activeCourses = userAssignments.filter(a => a.status === 'active').length;
        const inProgressCourses = userAssignments.filter(a => a.status === 'in_progress').length;
        const completedCourses = userAssignments.filter(a => a.status === 'completed').length;

        const gradesArray = userAssignments
          .map(a => a.final_grade)
          .filter((g): g is number => g !== null);
        const averageGrade = gradesArray.length > 0
          ? gradesArray.reduce((sum, g) => sum + g, 0) / gradesArray.length
          : null;

        const totalTimeSpent = userAssignments.reduce((sum, a) => sum + (a.total_time_spent || 0), 0);

        const activityDates = userAssignments
          .map(a => a.last_activity_date)
          .filter((d): d is string => d !== null);
        const lastActivityDate = activityDates.length > 0
          ? activityDates.sort().reverse()[0]
          : null;

        const completionRate = userAssignments.length > 0
          ? (completedCourses / userAssignments.length) * 100
          : 0;

        return {
          id: profile.id,
          name: profile.name || 'Unknown',
          email: profile.email || '',
          totalCourses: userAssignments.length,
          activeCourses,
          inProgressCourses,
          completedCourses,
          certificatesEarned: userCertificates.length,
          averageGrade,
          totalTimeSpent,
          lastActivityDate,
          completionRate,
        };
      });

      // Apply search filter
      if (filters?.searchQuery) {
        const query = filters.searchQuery.toLowerCase();
        return reportData.filter(
          student =>
            student.name.toLowerCase().includes(query) ||
            student.email.toLowerCase().includes(query)
        );
      }

      return reportData;
    } catch (error) {
      console.error('Error fetching all students report:', error);
      throw error;
    }
  },

  async fetchIndividualStudentReport(
    studentId: string,
    filters?: ReportFilters
  ): Promise<CourseAssignmentDetail[]> {
    try {
      // Fetch student profile
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('name, email')
        .eq('id', studentId)
        .single();

      if (profileError) throw profileError;

      // Fetch student's course assignments
      let assignmentQuery = supabase
        .from('course_assignments')
        .select(`
          id,
          course_id,
          status,
          assigned_date,
          completion_date,
          final_grade,
          total_time_spent
        `)
        .eq('user_id', studentId);

      // Apply filters
      if (filters?.dateFrom) {
        assignmentQuery = assignmentQuery.gte('assigned_date', filters.dateFrom);
      }
      if (filters?.dateTo) {
        assignmentQuery = assignmentQuery.lte('assigned_date', filters.dateTo);
      }
      if (filters?.statuses && filters.statuses.length > 0) {
        assignmentQuery = assignmentQuery.in('status', filters.statuses);
      }
      if (filters?.courseIds && filters.courseIds.length > 0) {
        assignmentQuery = assignmentQuery.in('course_id', filters.courseIds);
      }

      const { data: assignments, error: assignmentError } = await assignmentQuery;
      if (assignmentError) throw assignmentError;

      // Fetch course details
      const courseIds = assignments?.map(a => a.course_id) || [];
      const { data: courses, error: courseError } = await supabase
        .from('courses')
        .select('id, title, category, level')
        .in('id', courseIds);

      if (courseError) throw courseError;

      // Fetch certificates
      const { data: certificates, error: certError } = await supabase
        .from('certificates')
        .select('course_id')
        .eq('user_id', studentId);

      if (certError) throw certError;

      // Combine data
      const reportData: CourseAssignmentDetail[] = assignments?.map(assignment => {
        const course = courses?.find(c => c.id === assignment.course_id);
        const hasCertificate = certificates?.some(cert => cert.course_id === assignment.course_id) || false;

        return {
          id: assignment.id,
          studentName: profile.name || 'Unknown',
          studentEmail: profile.email || '',
          courseId: assignment.course_id,
          courseTitle: course?.title || 'Unknown Course',
          category: course?.category || 'Unknown',
          level: course?.level || 'intermediate',
          assignedDate: assignment.assigned_date,
          status: assignment.status,
          completionDate: assignment.completion_date,
          finalGrade: assignment.final_grade,
          timeSpent: assignment.total_time_spent || 0,
          hasCertificate,
        };
      }) || [];

      return reportData;
    } catch (error) {
      console.error('Error fetching individual student report:', error);
      throw error;
    }
  },

  async fetchAvailableCourses() {
    try {
      const { data: courses, error } = await supabase
        .from('courses')
        .select('id, title')
        .eq('is_active', true)
        .order('title');

      if (error) throw error;
      return courses || [];
    } catch (error) {
      console.error('Error fetching courses:', error);
      return [];
    }
  },

  async fetchAvailableStudents() {
    try {
      const { data: profiles, error } = await supabase
        .from('profiles')
        .select('id, name, email')
        .order('name');

      if (error) throw error;
      return profiles || [];
    } catch (error) {
      console.error('Error fetching students:', error);
      return [];
    }
  },
};
